import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Table, Button, Typography, Card, Spin, Modal, InputNumber, Input, message, Space, Tag, Select, Form, Tooltip } from 'antd';
import { PlusOutlined, WalletOutlined, DollarOutlined, QrcodeOutlined, PrinterOutlined, SearchOutlined, UserAddOutlined, TeamOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import QRCode from 'qrcode';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { getAllWallets, manualRechargeWallet, adminCreateChildWallet, WalletData } from '../../services/adminWalletService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function WalletsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<WalletData | null>(null);
  const [rechargeAmount, setRechargeAmount] = useState<number | null>(null);
  const [rechargeNotes, setRechargeNotes] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [walletFilter, setWalletFilter] = useState<'all' | 'parent' | 'child'>('all');
  const [childWalletModalVisible, setChildWalletModalVisible] = useState(false);
  const [childWalletForm] = Form.useForm();
  const [creatingChildWallet, setCreatingChildWallet] = useState(false);

  const loadWallets = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const result = await getAllWallets();
      setWallets(result.wallets);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading wallets:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadWallets();
      }
    });
    return unsubscribe;
  }, [navigate, loadWallets]);

  const handleRefresh = () => {
    loadWallets(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleRechargeClick = (wallet: WalletData) => {
    setSelectedWallet(wallet);
    setRechargeAmount(null);
    setRechargeNotes('');
    setRechargeModalVisible(true);
  };

  const handleQrCodeClick = async (wallet: WalletData) => {
    setSelectedWallet(wallet);

    // Generate QR code data - use the stored qrCode or generate from walletId
    const qrData = wallet.qrCode || JSON.stringify({ walletId: wallet.id });

    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
      setQrCodeDataUrl(dataUrl);
      setQrModalVisible(true);
    } catch (error) {
      message.error('Erro ao gerar QR Code');
    }
  };

  const handlePrintQrCode = () => {
    if (!selectedWallet || !qrCodeDataUrl) return;

    const isChild = selectedWallet.isChildWallet;
    const walletLabel = isChild ? 'Carteira Filho' : 'Carteira';
    const displayName = selectedWallet.name?.split(' ')[0] || selectedWallet.name;
    const parentInfo = isChild && selectedWallet.parentWalletName
      ? `<p style="font-size: 14px; color: #666;">Responsável: ${selectedWallet.parentWalletName}</p>`
      : '';
    const emailInfo = selectedWallet.email
      ? `<p>${selectedWallet.email}</p>`
      : '';

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR Code - ${displayName}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 20px;
              border: 2px dashed #ccc;
              border-radius: 16px;
              max-width: 350px;
            }
            .badge {
              display: inline-block;
              padding: 4px 12px;
              background: ${isChild ? '#1890ff' : '#52c41a'};
              color: white;
              border-radius: 12px;
              font-size: 12px;
              margin-bottom: 16px;
            }
            img {
              width: 280px;
              height: 280px;
            }
            h2 {
              margin: 20px 0 10px;
              color: #333;
              font-size: 24px;
            }
            p {
              margin: 5px 0;
              color: #666;
            }
            .instructions {
              margin-top: 20px;
              padding: 12px;
              background: #f5f5f5;
              border-radius: 8px;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <span class="badge">${walletLabel}</span>
            <img src="${qrCodeDataUrl}" alt="QR Code" />
            <h2>${displayName}</h2>
            ${parentInfo}
            ${emailInfo}
            <p style="font-size: 11px; color: #999;">ID: ${selectedWallet.id}</p>
            <div class="instructions">
              Apresente este QR Code no estande para realizar pagamentos
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleRechargeConfirm = async () => {
    if (!selectedWallet || !rechargeAmount || rechargeAmount <= 0) {
      message.error('Por favor, insira um valor válido');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      message.error('Usuário não autenticado');
      return;
    }

    setRecharging(true);
    try {
      const result = await manualRechargeWallet({
        walletId: selectedWallet.id,
        amount: rechargeAmount,
        adminUserId: user.uid,
        adminEmail: user.email || 'admin',
        notes: rechargeNotes,
      });

      message.success(
        `Recarga realizada com sucesso! Saldo anterior: R$ ${result.previousBalance.toFixed(2)} → Novo saldo: R$ ${result.newBalance.toFixed(2)}`
      );

      // Update local state
      setWallets(wallets.map(w =>
        w.id === selectedWallet.id
          ? { ...w, balance: result.newBalance }
          : w
      ));

      setRechargeModalVisible(false);
    } catch (error: any) {
      message.error(error.message || 'Erro ao realizar recarga');
    } finally {
      setRecharging(false);
    }
  };

  // Group wallets: parent followed by its children, then sort groups by parent name
  const groupedWallets = useMemo(() => {
    const parents = wallets.filter(w => !w.isChildWallet);
    const children = wallets.filter(w => w.isChildWallet);

    // Sort parents alphabetically by name
    parents.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    // Build grouped list: each parent followed by its children
    const result: WalletData[] = [];
    for (const parent of parents) {
      result.push(parent);
      const parentChildren = children.filter(c => c.parentWalletId === parent.id);
      parentChildren.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      result.push(...parentChildren);
    }

    // Add orphan children (no matching parent) at the end
    const assignedChildIds = new Set(result.filter(w => w.isChildWallet).map(w => w.id));
    const orphans = children.filter(c => !assignedChildIds.has(c.id));
    result.push(...orphans);

    return result;
  }, [wallets]);

  // Filter wallets based on search and filter criteria
  const filteredWallets = useMemo(() => {
    let filtered = groupedWallets;

    // Apply wallet type filter
    if (walletFilter === 'parent') {
      filtered = filtered.filter(w => !w.isChildWallet);
    } else if (walletFilter === 'child') {
      filtered = filtered.filter(w => w.isChildWallet);
    }

    // Apply search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.name?.toLowerCase().includes(search) ||
          w.email?.toLowerCase().includes(search) ||
          w.id.toLowerCase().includes(search) ||
          w.childName?.toLowerCase().includes(search) ||
          w.parentWalletName?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [groupedWallets, searchText, walletFilter]);

  // Parent wallets for dropdown (excluding filtered view)
  const parentWallets = useMemo(() => {
    return wallets.filter(w => !w.isChildWallet);
  }, [wallets]);

  const handleCreateChildWallet = async (values: { parentWalletId: string; childName: string }) => {
    setCreatingChildWallet(true);
    try {
      await adminCreateChildWallet({
        parentWalletId: values.parentWalletId,
        childName: values.childName,
      });

      message.success(`Carteira filho "${values.childName}" criada com sucesso!`);
      setChildWalletModalVisible(false);
      childWalletForm.resetFields();
      loadWallets();
    } catch (error: any) {
      message.error(error.message || 'Erro ao criar carteira filho');
    } finally {
      setCreatingChildWallet(false);
    }
  };

  const handleSearch = () => {
    // Search is already reactive via useMemo, this is just for UX feedback
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const columns: ColumnsType<WalletData> = [
    {
      title: 'Tipo',
      dataIndex: 'isChildWallet',
      key: 'type',
      width: 140,
      render: (isChild) => (
        isChild ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#ccc', marginRight: 8 }}>└</span>
            <Tag color="blue" icon={<TeamOutlined />}>Filho</Tag>
          </div>
        ) : (
          <Tag color="green">Principal</Tag>
        )
      ),
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name) => (
        <Text strong>{name}</Text>
      ),
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email, record) => (
        record.isChildWallet ? (
          <Text type="secondary">-</Text>
        ) : (
          email
        )
      ),
    },
    {
      title: 'Responsável',
      dataIndex: 'parentWalletName',
      key: 'parentWalletName',
      width: 150,
      render: (parentWalletName, record) => (
        record.isChildWallet ? (
          <Text>{parentWalletName || '-'}</Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Saldo',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance) => (
        <Text style={{ color: colors.success, fontWeight: 500 }}>
          R$ {balance.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'ID da Carteira',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => (
        <Text code style={{ fontSize: 12 }}>
          {id.substring(0, 12)}...
        </Text>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="default"
            icon={<QrcodeOutlined />}
            onClick={() => handleQrCodeClick(record)}
          >
            QR Code
          </Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => handleRechargeClick(record)}
            style={{ background: colors.info }}
          >
            Recarregar
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <WalletOutlined style={{ marginRight: 12 }} />
          Carteiras
        </Title>
        <Space>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Atualizado às {formatTime(lastUpdated)}
            </Text>
          )}
          <Tooltip title="Atualizar lista de carteiras">
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Atualizar
            </Button>
          </Tooltip>
          <Button
            type="default"
            icon={<UserAddOutlined />}
            size="large"
            onClick={() => setChildWalletModalVisible(true)}
          >
            Criar Carteira Filho
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/admin/create-wallet')}
            style={{ background: colors.success }}
          >
            Criar Nova Carteira
          </Button>
        </Space>
      </div>

      <Card style={{ background: colors.cardBackground, width: '100%' }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Buscar por nome, e-mail ou ID"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{ width: 300 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Buscar
          </Button>
          <Select
            value={walletFilter}
            onChange={setWalletFilter}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'Todas as Carteiras' },
              { value: 'parent', label: 'Apenas Principais' },
              { value: 'child', label: 'Apenas Filhos' },
            ]}
          />
        </Space>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            Mostrando {filteredWallets.length} de {wallets.length} carteiras
          </Text>
        </div>
        <Table
          columns={columns}
          dataSource={filteredWallets}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} carteiras`,
          }}
          locale={{ emptyText: 'Nenhuma carteira encontrada' }}
          rowClassName={(record) => record.isChildWallet ? 'child-wallet-row' : ''}
          scroll={{ x: 1300 }}
        />
        <style>{`
          .child-wallet-row {
            background-color: #fafafa;
          }
          .child-wallet-row td:first-child {
            padding-left: 32px !important;
          }
          .child-wallet-row:hover td {
            background-color: #f0f0f0 !important;
          }
        `}</style>
      </Card>

      <Modal
        title={
          <span>
            <DollarOutlined style={{ marginRight: 8, color: colors.info }} />
            Recarga Manual
          </span>
        }
        open={rechargeModalVisible}
        onCancel={() => setRechargeModalVisible(false)}
        onOk={handleRechargeConfirm}
        confirmLoading={recharging}
        okText="Confirmar Recarga"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: colors.success } }}
      >
        {selectedWallet && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>Carteira:</strong> {selectedWallet.name}</p>
            <p><strong>E-mail:</strong> {selectedWallet.email}</p>
            <p>
              <strong>Saldo atual:</strong>{' '}
              <Text style={{ color: colors.success }}>
                R$ {selectedWallet.balance.toFixed(2)}
              </Text>
            </p>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Valor da Recarga (R$):
          </label>
          <InputNumber
            style={{ width: '100%' }}
            min={0.01}
            step={0.01}
            precision={2}
            value={rechargeAmount}
            onChange={(value) => setRechargeAmount(value)}
            placeholder="0.00"
            size="large"
            prefix="R$"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Observações (opcional):
          </label>
          <TextArea
            rows={3}
            value={rechargeNotes}
            onChange={(e) => setRechargeNotes(e.target.value)}
            placeholder="Ex: Recarga para evento, correção de saldo..."
          />
        </div>

        {rechargeAmount && rechargeAmount > 0 && selectedWallet && (
          <div style={{
            marginTop: 16,
            padding: 12,
            background: colors.cardBackground,
            borderRadius: 8
          }}>
            <Text strong>Novo saldo após recarga: </Text>
            <Text style={{ color: colors.success, fontWeight: 'bold' }}>
              R$ {(selectedWallet.balance + rechargeAmount).toFixed(2)}
            </Text>
          </div>
        )}
      </Modal>

      <Modal
        title={
          <span>
            <QrcodeOutlined style={{ marginRight: 8, color: colors.primary }} />
            QR Code da Carteira
          </span>
        }
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setQrModalVisible(false)}>
            Fechar
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintQrCode}
            style={{ background: colors.primary }}
          >
            Imprimir
          </Button>,
        ]}
        width={400}
      >
        {selectedWallet && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              padding: 20,
              background: '#FFFFFF',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: 16,
            }}>
              {qrCodeDataUrl && (
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code"
                  style={{ width: 250, height: 250 }}
                />
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <Text strong style={{ fontSize: 18, display: 'block' }}>
                {selectedWallet.name}
              </Text>
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {selectedWallet.email}
              </Text>
              <Text
                code
                style={{ display: 'block', marginTop: 8, fontSize: 11 }}
              >
                ID: {selectedWallet.id}
              </Text>
              <div style={{
                marginTop: 12,
                padding: 8,
                background: colors.cardBackground,
                borderRadius: 8,
              }}>
                <Text>Saldo: </Text>
                <Text strong style={{ color: colors.success }}>
                  R$ {selectedWallet.balance.toFixed(2)}
                </Text>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8, color: colors.primary }} />
            Criar Carteira Filho
          </span>
        }
        open={childWalletModalVisible}
        onCancel={() => {
          setChildWalletModalVisible(false);
          childWalletForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={childWalletForm}
          layout="vertical"
          onFinish={handleCreateChildWallet}
        >
          <Form.Item
            name="parentWalletId"
            label="Carteira do Responsável"
            rules={[{ required: true, message: 'Selecione a carteira do responsável' }]}
          >
            <Select
              showSearch
              placeholder="Buscar por nome ou e-mail"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={parentWallets.map(w => ({
                value: w.id,
                label: `${w.name} (${w.email || 'Sem e-mail'}) - R$ ${w.balance.toFixed(2)}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="childName"
            label="Nome da Criança"
            rules={[
              { required: true, message: 'Digite o nome da criança' },
              { min: 2, message: 'O nome deve ter pelo menos 2 caracteres' }
            ]}
          >
            <Input placeholder="Ex: Maria, João..." />
          </Form.Item>

          <div style={{
            padding: 12,
            background: colors.infoBg,
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <Text style={{ fontSize: 13, color: colors.text }}>
              <strong>Dica:</strong> A carteira filho será criada com saldo zero.
              Após criar, use o botão "Recarregar" ou peça ao responsável
              transferir saldo pelo aplicativo.
            </Text>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setChildWalletModalVisible(false);
                childWalletForm.resetFields();
              }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={creatingChildWallet}
                style={{ background: colors.primary }}
              >
                Criar Carteira Filho
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
